import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { fmtDateTime } from "../lib/format";

// Preview + pick recipients before emailing pay statements for a run.
export function PayslipEmailDialog({ runId, onClose, onSent }) {
  const [pv, setPv] = useState(null);
  const [sampleId, setSampleId] = useState("");
  const [picked, setPicked] = useState(null);   // Set of employee_ids
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = sampleId ? `?employee_id=${sampleId}` : "";
    api.get(`/payroll/runs/${runId}/email/preview${q}`).then((r) => {
      setPv(r.data);
      if (!sampleId) setSampleId(r.data.sample_employee_id || "");
      if (!picked) setPicked(new Set(r.data.recipients.filter((x) => x.email).map((x) => x.employee_id)));
    }).catch((e) => { toast.error(e?.response?.data?.detail || apiErr(e)); onClose(); });
  }, [runId, sampleId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/payroll/runs/${runId}/email`, { employee_ids: [...picked] });
      const failed = r.results.filter((x) => x.status === "failed");
      if (failed.length) toast.warning(`Sent ${r.sent} · failed: ${failed.map((f) => f.name || f.email).join(", ")}`);
      else toast.success(`Pay statements emailed to ${r.sent} employee${r.sent === 1 ? "" : "s"}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || apiErr(e)); } finally { setBusy(false); }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="payslip-email-dialog" onClick={onClose}>
      <div className="bg-white border border-black w-full max-w-5xl max-h-[92vh] grid lg:grid-cols-[1fr_400px]" onClick={(e) => e.stopPropagation()}>
        <div className="border-r border-border">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate">Email preview · {pv?.subject}</div>
            <select data-testid="payslip-email-sample" value={sampleId} onChange={(e) => setSampleId(e.target.value)} className="border border-input px-2 py-1 text-xs focus:outline-none">
              {(pv?.recipients || []).map((r) => <option key={r.employee_id} value={r.employee_id}>{r.name}</option>)}
            </select>
          </div>
          {pv ? <iframe title="preview" data-testid="payslip-email-preview" srcDoc={pv.html} tabIndex={-1} className="w-full h-[calc(92vh-48px)] bg-neutral-50" /> : <div className="p-8 text-sm text-muted-foreground">Loading preview…</div>}
        </div>
        <div className="p-5 space-y-4 text-sm max-h-[92vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-bold tracking-tight flex items-center gap-2"><Mail className="h-4 w-4" /> Email pay statements</h3>
            <button onClick={onClose} aria-label="Close" data-testid="payslip-email-close" className="text-muted-foreground hover:text-black"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-xs text-muted-foreground">Each employee receives their own statement with a no-login PDF link. Untick anyone you don't want to email this time.</p>
          {pv && (
            <>
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <span>Recipients · {picked?.size ?? 0} of {pv.with_email} selected{pv.without_email ? ` · ${pv.without_email} no email` : ""}</span>
                <span className="flex gap-2">
                  <button data-testid="payslip-email-select-all" onClick={() => setPicked(new Set(pv.recipients.filter((x) => x.email).map((x) => x.employee_id)))} className="underline hover:text-black">All</button>
                  <button data-testid="payslip-email-select-none" onClick={() => setPicked(new Set())} className="underline hover:text-black">None</button>
                </span>
              </div>
              <ul className="border border-border divide-y divide-border/60 max-h-72 overflow-y-auto" data-testid="payslip-email-recipients">
                {pv.recipients.map((r) => (
                  <li key={r.employee_id} className={`px-3 py-2 flex items-center gap-3 text-xs ${r.email ? "" : "opacity-60"}`}>
                    <input type="checkbox" data-testid={`payslip-email-pick-${r.employee_id}`} disabled={!r.email} checked={!!r.email && picked?.has(r.employee_id)} onChange={() => toggle(r.employee_id)} className="h-4 w-4 accent-black" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{r.name}</div>
                      <div className={`font-mono truncate ${r.email ? "text-muted-foreground" : "text-red-700"}`}>{r.email || "no email on file"}</div>
                    </div>
                    <div className="font-mono tabular-nums">{fmt(r.net_pay)}</div>
                  </li>
                ))}
              </ul>
              {pv.last_sent_at && (
                <p className="text-[10px] text-emerald-700" data-testid="payslip-email-last">Last emailed {fmtDateTime(pv.last_sent_at)} by {pv.last_sent_by}
                  {" · "}{pv.last_results.filter((x) => x.status === "sent").length} sent{pv.last_results.some((x) => x.status === "failed") ? `, ${pv.last_results.filter((x) => x.status === "failed").length} failed` : ""}</p>
              )}
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button data-testid="payslip-email-send" onClick={send} disabled={busy || !picked?.size} className="flex-1 bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{busy ? "Sending…" : `Send to ${picked?.size ?? 0}`}</button>
            <button onClick={onClose} className="border border-border px-4 py-2.5 text-sm hover:border-black transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
